import * as React from 'react';
import * as api from 'app/shared/api';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from "redux";
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import { Select, Button, Spin, Alert } from 'antd';
import { School, ProductType, Product, Fee } from 'app/shared/model';
import { getSchools, getProducts, getFees } from 'app/pages/AgentPricing/modules/RefData';
import { ItemType } from 'app/pages/AgentPricing/actionTypes';
import { getRateFinderCriteria, getRateFinderResult } from 'app/pages/AgentPricing/modules/RateFinderWidget';
import { changeRateFinderCriteria } from 'app/pages/AgentPricing/actions';
import { RateFinderCriteria, RateFinderResult, RateFinderResultStatus } from 'app/pages/AgentPricing/modules/RateFinderWidget/state';
import { getRatesTable } from 'app/pages/AgentPricing/modules/RatesTableWidget/selectors';
import { findRateCommand } from 'app/pages/AgentPricing/commands';
import { isDefined } from 'app/helpers/objectHelper';
import { getSchoolKey } from "app/pages/AgentPricing/utils/schoolIds";
import {normalizeFeeCode, toFeeCode} from "app/pages/AgentPricing/utils/feeCodes";

const { Option } = Select;

export interface RateFinderWidgetProps {}

enum ItemsDropdownState {
    NoType,
    Loading,
    Error,
    Show,     
}

interface ItemOption {
    id: string, 
    name: string
}

interface ItemTypeOption {
    caption: string, 
    type: ItemType
}

const ItemTypeOptions : ItemTypeOption[] = [
    { caption: 'Course', type: ItemType.Course }, 
    { caption: 'Accommodation', type: ItemType.Accommodation }, 
    { caption: 'Add-on', type: ItemType.AddOn }, 
    { caption: 'Transfer', type: ItemType.Transfer }, 
    { caption: 'Fee', type: ItemType.Fee }
];

interface StateProps {
    schools: School[], 
    products: Product[], 
    fees: Fee[], 
    versionCode: string,
    criteria: RateFinderCriteria, 
    result: RateFinderResult
 }

interface DispatchProps {
    reset: () => void;
    findRateCommand: (versionCode: string, itemType?: ItemType, itemId?: string, schoolKey?: string) => void;
}

type InternalProps = StateProps & DispatchProps; 

interface InternalState {
    itemOptions: ItemOption[]
    itemDropdownState: ItemsDropdownState
} 

class RateFinderWidget extends React.Component<InternalProps, InternalState> {
    constructor(props: InternalProps) {
        super(props);
        this.state = {
            itemOptions: [],
            itemDropdownState: ItemsDropdownState.NoType
        }      
    }

    private normalizeSchoolKey(input?: string): string | undefined {
        if (!isDefined(input)) return undefined;

        const byKey = this.props.schools.find(s => getSchoolKey(s) === input);
        if (byKey) return input;

        const byCode = this.props.schools.find(s => s.code === input);
        if (byCode) return getSchoolKey(byCode);

        return input;
    }

    private changeSchool(schoolKey: string) {
        const normalized = this.normalizeSchoolKey(schoolKey);
        const current = this.normalizeSchoolKey(this.props.criteria.schoolId);

        if (normalized === current) return;

        this.update(normalized, this.props.criteria.itemType, this.props.criteria.itemId);
    }

    private changeType(type: ItemType) {
        if (type === this.props.criteria.itemType) return;
        this.update(this.normalizeSchoolKey(this.props.criteria.schoolId), type, undefined);
    }

    private changeItem(itemId: string) {
        if (itemId === this.props.criteria.itemId)
            return;

        const normalizedItemId =
            this.props.criteria.itemType === ItemType.Fee
                ? (normalizeFeeCode(itemId) ?? itemId)
                : itemId;

        this.props.findRateCommand(
            this.props.versionCode,
            this.props.criteria.itemType,
            normalizedItemId,
            this.normalizeSchoolKey(this.props.criteria.schoolId)
        );
    }

    private getProductType(itemType: ItemType) : ProductType|undefined {
        switch(itemType) {
            case ItemType.Course: 
                return ProductType.Course; 
            case ItemType.Accommodation: 
                return ProductType.Accommodation; 
            case ItemType.Transfer: 
                return ProductType.Transfer; 
            case ItemType.AddOn: 
                return ProductType.AddOn; 
            case ItemType.Fee: 
                return undefined; 
            default: 
                throw new Error('Unknown item tpye: ' + itemType)
        }
    }

    private update(schoolKey?: string, itemType?: ItemType, itemId?: string) {
        schoolKey = this.normalizeSchoolKey(schoolKey);

        this.setState({
            itemDropdownState: ItemsDropdownState.Loading
        });

        if (!isDefined(itemType)) {
            this.setState({
                itemOptions: [],
                itemDropdownState: ItemsDropdownState.NoType
            });

            this.props.findRateCommand(this.props.versionCode, itemType, itemId, schoolKey);
            return;
        }

        if (itemType === ItemType.Fee) {
            const normalized = normalizeFeeCode(itemId ?? null);
            itemId = normalized;

            this.setState({
                itemOptions: this.props.fees.map(f => ({ id: toFeeCode(f), name: f.name })),
                itemDropdownState: ItemsDropdownState.Show
            });

            this.props.findRateCommand(this.props.versionCode, itemType, itemId, schoolKey);
            return;
        }

        const productType = this.getProductType(itemType);
        const products = this.props.products.filter(x => x.type === productType);

        const schoolCode = schoolKey
            ? this.props.schools.find(s => getSchoolKey(s) === schoolKey)?.code
            : undefined;

        if (isDefined(schoolCode)) {
            api.getOfferingsMinimal([schoolCode])
                .then(offerings => {
                    const productOfferings = products.filter(p => offerings.some(o => o.productId === p.code));
                    const item = productOfferings.find(x => x.code === itemId);
                    itemId = isDefined(item) ? item.code : undefined;

                    this.setState({
                        itemOptions: productOfferings.map(x => ({ id: x.code, name: x.name })),
                        itemDropdownState: ItemsDropdownState.Show
                    });

                    this.props.findRateCommand(this.props.versionCode, itemType, itemId, schoolKey);
                })
                .catch(() => {
                    itemId = undefined;

                    this.setState({
                        itemOptions: [],
                        itemDropdownState: ItemsDropdownState.Error
                    });

                    this.props.findRateCommand(this.props.versionCode, itemType, itemId, schoolKey);
                });
        } else {
            const item = products.find(x => x.code === itemId);
            itemId = isDefined(item) ? item.code : undefined;

            this.setState({
                itemOptions: products.map(x => ({ id: x.code, name: x.name })),
                itemDropdownState: ItemsDropdownState.Show
            });

            this.props.findRateCommand(this.props.versionCode, itemType, itemId, schoolKey);
        }
    }

    renderResult() {
        switch (this.props.result.status) {
            case RateFinderResultStatus.Running:
                return (
                    <Spin description="Finding the rate..." size="large">
                        <div style={{ padding: 50 }} />
                    </Spin>
                );

            case RateFinderResultStatus.Errored:
                return (
                    <Alert
                        title="We're sorry"
                        description="An error occurred when trying to find the correct rate, please try again."
                        type="error"
                        showIcon
                    />
                );

            case RateFinderResultStatus.Partial:
                return (
                    <Alert
                        title="Table updated"
                        description="The rates table has been updated, highlighting the required column or row."
                        type="success"
                        showIcon
                    />
                );

            case RateFinderResultStatus.NotFound:
                return (
                    <Alert
                        title="Rate: 0%"
                        description="The selected product is not mapped to any of the listed groups and therefore will have no rate applied."
                        type="warning"
                        showIcon
                    />
                );

            case RateFinderResultStatus.Found:
                return (
                    <Alert
                        title={`Rate: ${this.props.result.rate}`}
                        description="The correct column and row has been highlighed in the rates table."
                        type="success"
                        showIcon
                    />
                );

            case RateFinderResultStatus.Empty:
            default:
                return <div />;
        }
    }

    renderForm() {
        const labelStyle = { display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500, color: '#262626' };
        const selectedSchoolValue = this.normalizeSchoolKey(this.props.criteria.schoolId);

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', gap: 16 }}>
                    <div style={{ flex: 1 }}>
                        <label style={labelStyle}>Type</label>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Select type"
                            onChange={(value: ItemType) => this.changeType(value)}
                            value={this.props.criteria.itemType}
                        >
                            {ItemTypeOptions.map(x => (
                                <Option key={x.type} value={x.type}>
                                    {x.caption}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div style={{ flex: 3 }}>
                        <label style={labelStyle}>Item</label>
                        <Select
                            style={{ width: '100%' }}
                            placeholder={this.state.itemDropdownState === ItemsDropdownState.NoType
                                ? 'Please select the item type'
                                : 'Select a product or fee'}
                            value={this.props.criteria.itemId}
                            onChange={(value: string) => this.changeItem(value)}
                            loading={this.state.itemDropdownState === ItemsDropdownState.Loading}
                            disabled={this.state.itemDropdownState !== ItemsDropdownState.Show}
                        >
                            {this.state.itemOptions.map(x => (
                                <Option key={x.id} value={x.id}>
                                    {x.name}
                                </Option>
                            ))}
                        </Select>
                    </div>
                </div>

                <div style={{ marginTop: 16 }}>
                    <label style={labelStyle}>School</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select school"
                        onChange={(value: string) => this.changeSchool(value)}
                        value={selectedSchoolValue}
                    >
                        {this.props.schools.map(s => {
                            const k = getSchoolKey(s);
                            return (
                                <Option key={k} value={k}>
                                    {s.name}
                                </Option>
                            );
                        })}
                    </Select>
                </div>

                <div style={{ marginTop: 16, textAlign: 'right' }}>
                    <Button
                        onClick={() => this.props.reset()}
                        disabled={
                            !isDefined(this.props.criteria.itemId) &&
                            !isDefined(this.props.criteria.itemType) &&
                            !isDefined(this.props.criteria.schoolId)
                        }
                    >
                        Reset
                    </Button>
                </div>
            </div>
        );
    }

    componentDidMount() {
        this.update(
            this.normalizeSchoolKey(this.props.criteria.schoolId),
            this.props.criteria.itemType,
            this.props.criteria.itemId
        ); 
    }

    render() {
        return (
            <div style={{ marginTop: 24, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px', overflow: 'hidden' }}>
                <ComponentHeader text="Rate Finder" />
                <div style={{ padding: '16px' }}>
                    {this.renderResult()}     
                    {this.renderForm()}
                </div>
            </div>
        );
    }
} 

const mapStateToProps = (state:any, ownProps: RateFinderWidgetProps) : StateProps  => {
    const ratesTable = getRatesTable(state)

    return { 
        schools: getSchools(state), 
        products: getProducts(state), 
        fees: getFees(state), 
        versionCode: isDefined(ratesTable) ? ratesTable.versionCode : '',
        criteria: getRateFinderCriteria(state), 
        result: getRateFinderResult(state)
    }
};

const mapDispatchToProps = (dispatch:ThunkDispatch<any, any, Action>, ownProps: RateFinderWidgetProps) : DispatchProps => ({
    reset: () => dispatch(changeRateFinderCriteria(undefined, undefined, undefined)),
    findRateCommand: (versionCode: string, itemType?: ItemType, itemId?: string, schoolKey?: string) =>
        dispatch(findRateCommand(versionCode, itemType, itemId, schoolKey))
})

export default connect(mapStateToProps, mapDispatchToProps)(RateFinderWidget);
